import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';
import { BarChart3, Brain, BookOpen, Target, Flame, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

export default function AnalyticsPage() {
  const { getAuthHeader, API } = useAuth();
  const { t, i18n } = useTranslation();
  const [analytics, setAnalytics] = useState(null);
  const [activityData, setActivityData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [dashboardRes, activityRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`, { headers: getAuthHeader() }),
        axios.get(`${API}/analytics/activity`, { headers: getAuthHeader() })
      ]);
      setAnalytics(dashboardRes.data);
      setActivityData(activityRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="analytics-page">
        {/* Header */}
        <div>
          <h1 className="font-serif font-bold text-2xl md:text-3xl text-foreground">Performance Analytics</h1>
          <p className="text-muted-foreground">Track your mental training journey</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="bg-card/50 border-white/5">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-orange-500/20">
                    <Flame className="h-5 w-5 text-orange-500" />
                  </div>
                  <span className="text-sm text-muted-foreground">Current Streak</span>
                </div>
                <p className="font-serif text-3xl font-bold text-foreground" data-testid="streak-stat">
                  {loading ? '-' : analytics?.streak || 0}
                  <span className="text-lg text-muted-foreground ml-1">days</span>
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <Card className="bg-card/50 border-white/5">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-secondary/20">
                    <Brain className="h-5 w-5 text-secondary" />
                  </div>
                  <span className="text-sm text-muted-foreground">Total Sessions</span>
                </div>
                <p className="font-serif text-3xl font-bold text-foreground" data-testid="total-sessions-stat">
                  {loading ? '-' : analytics?.meditation_sessions || 0}
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="bg-card/50 border-white/5">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-primary/20">
                    <TrendingUp className="h-5 w-5 text-primary" />
                  </div>
                  <span className="text-sm text-muted-foreground">Minutes Trained</span>
                </div>
                <p className="font-serif text-3xl font-bold text-foreground" data-testid="minutes-stat">
                  {loading ? '-' : analytics?.total_meditation_minutes || 0}
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            <Card className="bg-card/50 border-white/5">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-accent/20">
                    <BookOpen className="h-5 w-5 text-accent" />
                  </div>
                  <span className="text-sm text-muted-foreground">Journal Entries</span>
                </div>
                <p className="font-serif text-3xl font-bold text-foreground" data-testid="journal-stat">
                  {loading ? '-' : analytics?.journal_entries || 0}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Activity Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="bg-card/50 border-white/5">
            <CardHeader>
              <CardTitle className="font-serif text-xl flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary" />
                30-Day Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]" data-testid="activity-chart">
                {activityData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={activityData}>
                      <defs>
                        <linearGradient id="colorMeditations" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(160, 84%, 39%)" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="hsl(160, 84%, 39%)" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorJournals" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(262, 83%, 58%)" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="hsl(262, 83%, 58%)" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={formatDate}
                        stroke="hsl(240, 5%, 45%)"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis 
                        stroke="hsl(240, 5%, 45%)"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'hsl(240, 10%, 6%)',
                          border: '1px solid hsl(240, 5%, 15%)',
                          borderRadius: '8px',
                          color: 'hsl(0, 0%, 98%)'
                        }}
                        labelFormatter={formatDate}
                      />
                      <Area
                        type="monotone"
                        dataKey="meditations"
                        stroke="hsl(160, 84%, 39%)"
                        fillOpacity={1}
                        fill="url(#colorMeditations)"
                        name="Meditations"
                      />
                      <Area
                        type="monotone"
                        dataKey="journals"
                        stroke="hsl(262, 83%, 58%)"
                        fillOpacity={1}
                        fill="url(#colorJournals)"
                        name="Journal Entries"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    No activity data yet. Start training to see your progress!
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Goals Progress */}
        <div className="grid md:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
          >
            <Card className="bg-card/50 border-white/5 h-full">
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  Goals Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Total Goals</span>
                    <span className="font-serif text-2xl font-bold text-foreground">
                      {loading ? '-' : analytics?.total_goals || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Completed</span>
                    <span className="font-serif text-2xl font-bold text-secondary">
                      {loading ? '-' : analytics?.completed_goals || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Completion Rate</span>
                    <span className="font-serif text-2xl font-bold text-primary">
                      {loading ? '-' : `${analytics?.goals_completion_rate || 0}%`}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="bg-card/50 border-white/5 h-full">
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <Brain className="h-5 w-5 text-secondary" />
                  Weekly Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Sessions This Week</span>
                    <span className="font-serif text-2xl font-bold text-foreground">
                      {loading ? '-' : analytics?.weekly_meditations || 0}
                    </span>
                  </div>
                  <div className="p-4 bg-primary/10 rounded-xl border border-primary/20">
                    <p className="text-sm text-muted-foreground mb-1">Pro Tip</p>
                    <p className="text-foreground">
                      Aim for at least 3 meditation sessions per week for optimal mental performance benefits.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
}
